import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  AccountSubdomainResponseType,
  AccountSubdomainType,
  AccountUserType,
  CustomDomainDetailsType,
  DkimListType,
  LimitResetValueType,
  MutationType,
  TenantInfoType,
  UserType,
} from 'shared/types';
import request from '../../../utils/request';
import i18n from '../../../i18n';

export const useAddCustomDomain = ({ setToast, callback }: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (payload: any) => {
      const data = await request(
        '/accounts/mine/custom-domain/add',
        payload,
        'POST',
      );
      return data;
    },
    {
      onSuccess: (response: CustomDomainDetailsType) => {
        queryClient.invalidateQueries('accountinfo');
        if (callback) callback(true, response);
        if (!response?.error) {
          if (setToast)
            setToast({
              type: 'success',
              delay: 3000,
              text: i18n.t('settings.toasts.cname_generated'),
            });
        } else {
          if (setToast)
            setToast({
              type: 'error',
              delay: 3000,
              text: i18n.t('settings.toasts.cname_generation_failed'),
            });
        }
      },
      onError: (error) => {
        if (callback) callback(false, error);
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: i18n.t('settings.toasts.cname_generation_failed'),
          });
      },
    },
  );
};

export const useVerifyCustomDomain = ({ setToast, callback }: MutationType) => {
  const queryClient = useQueryClient();

  return useMutation(
    async () => {
      const data = await request('/accounts/mine/custom-domain/verify');
      return data;
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('accountinfo');
        if (callback) callback(true, response);
        if (!response?.error) {
          if (setToast)
            setToast({
              type: 'success',
              delay: 3000,
              text: i18n.t('settings.toasts.cname_verified'),
            });
        } else {
          if (setToast)
            setToast({
              type: 'error',
              delay: 3000,
              text: i18n.t('settings.toasts.cname_verification_failed'),
            });
        }
      },
      onError: (error) => {
        if (callback) callback(false, error);
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: i18n.t('settings.toasts.cname_verification_failed'),
          });
      },
    },
  );
};

export const useRequestCertificateCustomDomain = ({
  setToast,
  callback,
}: MutationType) => {
  const queryClient = useQueryClient();

  return useMutation(
    async () => {
      const data = await request(
        '/accounts/mine/custom-domain/request-certificate',
      );
      return data;
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('accountinfo');
        if (callback) callback(true, response);
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: i18n.t('settings.toasts.ssl_requested_successfully'),
          });
      },
      onError: (error) => {
        if (callback) callback(false, error);
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: i18n.t('settings.toasts.ssl_request_failed'),
          });
      },
    },
  );
};

export const useRequestCertificateStatusCustomDomain = ({
  callback,
}: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation(
    async () => {
      const data = await request(
        '/accounts/mine/custom-domain/request-certificate-status',
      );
      return data;
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('accountinfo');
        if (callback) callback(true, response);
      },
      onError: (error) => {
        if (callback) callback(false, error);
      },
    },
  );
};

export const useActivateCustomDomain = ({
  setToast,
  callback,
}: MutationType) => {
  const queryClient = useQueryClient();

  return useMutation(
    async () => {
      const data = await request('/accounts/mine/custom-domain/activate');
      return data;
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('accountinfo');
        if (callback) callback(true, response);
        if (!response?.error) {
          if (setToast)
            setToast({
              type: 'success',
              delay: 3000,
              text: i18n.t('settings.toasts.custom_domain_activated'),
            });
        } else {
          if (setToast)
            setToast({
              type: 'error',
              delay: 3000,
              text: i18n.t('settings.toasts.custom_domain_activation_failed'),
            });
        }
      },
      onError: (error) => {
        if (callback) callback(false, error);
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: i18n.t('settings.toasts.custom_domain_activation_failed'),
          });
      },
    },
  );
};

export const useRemoveCustomDomain = ({ setToast, callback }: MutationType) => {
  const queryClient = useQueryClient();

  return useMutation(
    async () => {
      const data = await request('/accounts/mine/custom-domain/remove');
      return data;
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('accountinfo');
        if (callback) callback(true, response);
        if (!response?.error) {
          if (setToast)
            setToast({
              type: 'success',
              delay: 3000,
              text: i18n.t('settings.toasts.custom_domain_removed'),
            });
        } else {
          if (setToast)
            setToast({
              type: 'error',
              delay: 3000,
              text: i18n.t('settings.toasts.custom_domain_removal_failed'),
            });
        }
      },
      onError: (error) => {
        if (callback) callback(false, error);
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: i18n.t('settings.toasts.custom_domain_removal_failed'),
          });
      },
    },
  );
};

export const useGetDkimList = ({ enabled }: MutationType) => {
  return useQuery(
    'dkim-list',
    async () => {
      const data: DkimListType = await request('/accounts/mine/dkim');
      return data;
    },
    { enabled },
  );
};

export const useCheckDomainAvailability = ({ callback }: MutationType) => {
  return useMutation(
    async (payload: AccountSubdomainType) => {
      const data: AccountSubdomainResponseType = await request(
        '/subdomain-availability',
        payload,
        'POST',
      );
      return data;
    },
    {
      onSuccess: (response, payload) => {
        if (callback) callback(true, response, payload);
      },
      onError: (error) => {
        if (callback) callback(false, error);
      },
    },
  );
};

export const useVerifyDKIM = ({ setToast }: MutationType) => {
  const queryClient = useQueryClient();

  return useMutation(
    async () => {
      const data = await request('/accounts/mine/dkim-verify');
      return data;
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('accountinfo');
        queryClient.invalidateQueries('dkim-list');
        if (setToast)
          setToast({
            type: response.verified ? 'success' : 'error',
            delay: 3000,
            text: response.verified
              ? i18n.t('settings.toasts.dkim_verified')
              : i18n.t('settings.toasts.dkim_verification_failed'),
          });
      },
      onError: () => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: i18n.t('settings.toasts.dkim_verification_failed'),
          });
      },
    },
  );
};
export const useDeleteDkim = ({ setToast, callback }: MutationType) => {
  return useMutation(
    async () => {
      const data = await request('/accounts/mine/dkim-remove');
      return data;
    },
    {
      onSuccess: () => {
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: i18n.t('settings.toasts.dkim_removed'),
          });
        if (callback) callback(true);
      },
      onError: () => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: i18n.t('settings.toasts.dkim_removal_failed'),
          });
        if (callback) callback(false);
      },
    },
  );
};

export const useGetCurrentUser = ({
  refetchOnWindowFocus = false,
  callback,
}: MutationType) =>
  useQuery(
    'loggedInUser',
    async () => {
      const data: UserType = await request('/users/me');
      return data;
    },
    {
      refetchOnWindowFocus,
      staleTime: refetchOnWindowFocus ? 10000 : 300000,
      onSuccess: (response: UserType) => {
        if (callback) callback(true, response);
      },
      onError: (error: any) => {
        if (callback) callback(false, error);
      },
    },
  );

export const useGetCurrentAccountUser = (
  userId: string,
  refetchOnWindowFocus = false,
) =>
  useQuery(
    ['account-user', userId],
    async () => {
      const data: AccountUserType = await request(
        `/accounts/mine/users/${userId}`,
      );
      return data;
    },
    {
      refetchOnWindowFocus,
      staleTime: refetchOnWindowFocus ? 10000 : 300000,
      enabled: !!userId,
    },
  );

export const useGetTenantInfo = () =>
  useQuery<TenantInfoType>('tenants', async () => {
    const data: TenantInfoType = await request('/tenants/mine');
    return data;
  });

type SaveTenantType = {
  payload: TenantInfoType;
};

export const useSaveTenantInfo = ({ setToast, callback }: MutationType) => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ payload }: SaveTenantType) => {
      const data = await request('/tenants/mine', payload, 'PUT');
      return data;
    },
    {
      onSuccess: (response: TenantInfoType, variables: SaveTenantType) => {
        queryClient.invalidateQueries('tenants');
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: i18n.t('settings.toasts.survey_limit_updated'),
          });
        if (callback) callback(true, response, variables);
      },
      onError: (error) => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: i18n.t('settings.toasts.survey_limit_updation_failed'),
          });
        if (callback) callback(false, error);
      },
    },
  );
};

export const useResetAPIKey = ({ setToast }: MutationType) => {
  const queryClient = useQueryClient();

  return useMutation(
    async () => {
      const data = await request('/accounts/mine/reset-api-key', {}, 'PUT');
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('accountinfo');
      },
      onError: () => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: i18n.t('settings.toasts.reset_api_key_failed'),
          });
      },
    },
  );
};

export const useResetHashKey = ({ setToast }: MutationType) => {
  const queryClient = useQueryClient();

  return useMutation(
    async () => {
      const data = await request('/tenants/mine/generateSecretKey', {}, 'POST');
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('tenants');
      },
      onError: () => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: i18n.t('settings.toasts.reset_email_hash_key_failed'),
          });
      },
    },
  );
};

export const useDeleteAccount = ({ setToast, callback }: MutationType) => {
  return useMutation(
    async () => {
      const data = await request('/accounts/mine', {}, 'DELETE');
      return data;
    },
    {
      onSuccess: (response: any) => {
        localStorage.clear();
        window.location.href = window.location.origin;
        if (callback) callback(true, response);
      },
      onError: (error: any) => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: i18n.t('settings.toasts.account_deletion_failed'),
          });
        if (callback) callback(false, error);
      },
    },
  );
};

export const useUpdateUserInfo = ({ setToast }: MutationType) => {
  const queryClient = useQueryClient();

  return useMutation(
    async (payload: any) => {
      const data = await request('/users/me', payload, 'PATCH');
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('loggedInUser');
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: i18n.t('settings.toasts.user_signature_updated'),
          });
      },
      onError: () => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: i18n.t('settings.toasts.user_signature_updation_failed'),
          });
      },
    },
  );
};
export const useTransferOwnership = ({ setToast, callback }: MutationType) => {
  const queryClient = useQueryClient();

  return useMutation(
    async (userId: any) => {
      const data = await request(
        `/accounts/mine/transfer-ownership/${userId}`,
        {},
        'POST',
      );
      return data;
    },
    {
      onSuccess: (response: any) => {
        queryClient.invalidateQueries('loggedInUser');
        queryClient.invalidateQueries('users');
        queryClient.invalidateQueries('account-user');
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: i18n.t('settings.toasts.owner_transferred'),
          });
        if (callback) callback(true, response);
      },
      onError: (error: any) => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: i18n.t('settings.toasts.owner_transfership_failed'),
          });
        if (callback) callback(false, error);
      },
    },
  );
};

export const useAccountSubdomainchange = ({ callback }: MutationType) => {
  return useMutation(
    async (payload: AccountSubdomainType) => {
      const data: unknown = await request(
        '/accounts/mine/subdomain-change',
        payload,
        'POST',
      );
      return data;
    },
    {
      onSuccess: (response, payload) => {
        if (callback) callback(true, response, payload);
      },
      onError: (error, variables) => {
        if (callback) callback(false, error, variables);
      },
    },
  );
};

export const useGetLimitResetValue = ({ setToast }) => {
  return useQuery<LimitResetValueType>(
    'limitReset',
    async () => {
      const data = await request('/tenants/mine/limit-reset', {}, 'GET');
      return data;
    },
    {
      onError: (error: any) => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: `${i18n.t('settings.toasts.limit_reset_failed')}`,
          });
      },
    },
  );
};
